import { getAuthToken } from '../../Api/authToken';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { FiTrash2, FiUserPlus, FiX, FiInbox, FiEye, FiEdit2, FiChevronDown, FiCheck, FiMapPin, FiMap, FiSearch } from 'react-icons/fi';
import './AdminPages.css';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  phone: '',
  state_ids: [],
  city_ids: [],
  sub_registers: [],
  own_access: false,
  chat_access: true,
  user_chat_access: false,
};

/* ─── Reusable Multi-Select Dropdown ───────────────────────────────────────── */
const MultiSelectDropdown = ({
  label,
  icon: Icon,
  options,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
  placeholder,
  emptyText,
  resolveLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setSearch('');
      window.setTimeout(() => searchRef.current?.focus?.(), 0);
    }
  }, [open]);

  const normSelected = [...new Set((selectedIds || []).map(Number).filter((n) => Number.isFinite(n)))];
  const optionById = new Map(options.map((o) => [Number(o.id), o]));
  const labelKey = options[0] ? (options[0].state_name ? 'state_name' : 'city_name') : 'name';
  const chipLabel = (id) => {
    const opt = optionById.get(Number(id));
    if (opt) return opt[labelKey] || opt.name || `#${id}`;
    if (resolveLabel) return resolveLabel(id);
    return `#${id}`;
  };

  const q = search.trim().toLowerCase();
  const filteredOptions = q
    ? options.filter((option) => String(option[labelKey] || option.name || '').toLowerCase().includes(q))
    : options;

  const filteredIds = filteredOptions.map((o) => Number(o.id));
  const allFilteredSelected = filteredIds.length > 0
    && filteredIds.every((id) => normSelected.includes(id));

  return (
    <div className="msd-wrapper" ref={ref}>
      <label className="msd-label">{Icon && <Icon size={13} />} {label}</label>
      <div className={`msd-trigger ${open ? 'open' : ''}`} onClick={() => options.length > 0 && setOpen(o => !o)}>
        <div className="msd-chips">
          {normSelected.length === 0 ? (
            <span className="msd-placeholder">{options.length === 0 ? emptyText : placeholder}</span>
          ) : (
            normSelected.map((id) => (
              <span key={id} className="msd-chip">
                {chipLabel(id)}
                <button type="button" className="msd-chip-remove" onClick={e => { e.stopPropagation(); onToggle(id); }}>
                  <FiX size={10} />
                </button>
              </span>
            ))
          )}
        </div>
        <FiChevronDown size={15} className={`msd-arrow ${open ? 'rotated' : ''}`} />
      </div>
      {open && (
        <div className="msd-dropdown">
          <div className="msd-toolbar" onClick={(e) => e.stopPropagation()}>
            <div className="msd-search">
              <FiSearch size={13} />
              <input
                ref={searchRef}
                type="text"
                value={search}
                placeholder="Search..."
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {search ? (
                <button type="button" className="msd-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                  <FiX size={12} />
                </button>
              ) : null}
            </div>
            <div className="msd-toolbar-actions">
              <button
                type="button"
                className="msd-action-btn"
                disabled={!filteredIds.length || typeof onSelectAll !== 'function'}
                onClick={() => {
                  if (allFilteredSelected && typeof onClearAll === 'function') {
                    onClearAll(filteredIds);
                  } else if (typeof onSelectAll === 'function') {
                    onSelectAll(filteredIds);
                  }
                }}
              >
                {allFilteredSelected ? 'Clear' : 'Select All'}
                {q ? ' filtered' : ''}
              </button>
              {normSelected.length > 0 && typeof onClearAll === 'function' ? (
                <button
                  type="button"
                  className="msd-action-btn"
                  onClick={() => onClearAll(normSelected)}
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>
          <div className="msd-search-hint">
            {filteredOptions.length} of {options.length} option{options.length !== 1 ? 's' : ''}
            {normSelected.length > 0 ? ` · ${normSelected.length} selected` : ''}
          </div>
          <ul className="msd-list">
            {filteredOptions.length === 0 ? (
              <li className="msd-option msd-option-empty">No matches</li>
            ) : filteredOptions.map(option => {
              const oid = Number(option.id);
              const isSelected = selectedIds.map(Number).includes(oid);
              return (
                <li
                  key={option.id}
                  className={`msd-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => onToggle(oid)}
                >
                  <span className={`msd-checkbox ${isSelected ? 'checked' : ''}`}>
                    {isSelected && <FiCheck size={10} />}
                  </span>
                  {option[labelKey]}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ─── City-Grouped Sub-Register Selector ───────────────────────────────────── */
const CityGroupedSubRegisters = ({
  formSubRegisters,
  selectedCityIds,
  selectedSRIds,
  onToggleSR,
  onSetSRs,
  getCityName,
}) => {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();

  const citiesWithSR = selectedCityIds
    .map(cityId => ({
      cityId,
      cityName: getCityName(cityId),
      subRegs: formSubRegisters.filter(sr => Number(sr.city_id) === Number(cityId))
    }))
    .filter(group => group.subRegs.length > 0)
    .map((group) => ({
      ...group,
      subRegs: q
        ? group.subRegs.filter((sr) => String(sr.sub_name || '').toLowerCase().includes(q))
        : group.subRegs,
    }))
    .filter((group) => group.subRegs.length > 0);

  const citiesWithNoSR = selectedCityIds.filter(
    cityId => !formSubRegisters.some(sr => Number(sr.city_id) === Number(cityId))
  );

  const allVisibleIds = citiesWithSR.flatMap((g) => g.subRegs.map((sr) => Number(sr.id)));
  const selectedNorm = selectedSRIds.map(Number);
  const allVisibleSelected = allVisibleIds.length > 0
    && allVisibleIds.every((id) => selectedNorm.includes(id));

  if (selectedCityIds.length === 0) {
    return (
      <div className="sr-empty-state">
        <FiMapPin size={20} />
        <span>Select a city above to assign sub-registers</span>
      </div>
    );
  }

  if (citiesWithSR.length === 0 && citiesWithNoSR.length > 0 && !q) {
    return (
      <div className="sr-empty-state">
        <span>No sub-registers available for the selected cities</span>
      </div>
    );
  }

  return (
    <div className="sr-city-groups">
      <div className="sr-toolbar">
        <div className="msd-search sr-search">
          <FiSearch size={13} />
          <input
            type="text"
            value={search}
            placeholder="Search sub-registers..."
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button type="button" className="msd-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
              <FiX size={12} />
            </button>
          ) : null}
        </div>
        {typeof onSetSRs === 'function' && allVisibleIds.length > 0 ? (
          <button
            type="button"
            className="msd-action-btn"
            onClick={() => {
              if (allVisibleSelected) {
                const drop = new Set(allVisibleIds);
                onSetSRs(selectedNorm.filter((id) => !drop.has(id)));
              } else {
                onSetSRs([...new Set([...selectedNorm, ...allVisibleIds])]);
              }
            }}
          >
            {allVisibleSelected ? 'Clear' : 'Select All'}
            {q ? ' filtered' : ''}
          </button>
        ) : null}
      </div>

      {citiesWithSR.length === 0 ? (
        <div className="sr-empty-state">
          <span>No sub-registers match “{search}”</span>
        </div>
      ) : citiesWithSR.map(({ cityId, cityName, subRegs }) => {
        const cityIds = subRegs.map((sr) => Number(sr.id));
        const cityAllSelected = cityIds.every((id) => selectedNorm.includes(id));
        return (
          <div key={cityId} className="sr-city-group">
            <div className="sr-city-header">
              <FiMap size={12} />
              <span>{cityName}</span>
              <span className="sr-city-count">{subRegs.filter(sr => selectedNorm.includes(Number(sr.id))).length}/{subRegs.length}</span>
              {typeof onSetSRs === 'function' ? (
                <button
                  type="button"
                  className="msd-action-btn sr-city-select-all"
                  onClick={() => {
                    if (cityAllSelected) {
                      const drop = new Set(cityIds);
                      onSetSRs(selectedNorm.filter((id) => !drop.has(id)));
                    } else {
                      onSetSRs([...new Set([...selectedNorm, ...cityIds])]);
                    }
                  }}
                >
                  {cityAllSelected ? 'Clear' : 'Select all'}
                </button>
              ) : null}
            </div>
            <div className="sr-pill-row">
              {subRegs.map(sr => (
                <button
                  key={sr.id}
                  type="button"
                  className={`sr-pill ${selectedNorm.includes(Number(sr.id)) ? 'active' : ''}`}
                  onClick={() => onToggleSR(sr.id)}
                >
                  {selectedNorm.includes(Number(sr.id)) && <FiCheck size={11} />}
                  {sr.sub_name}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Main Component ────────────────────────────────────────────────────────── */
const PartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingPartner, setViewingPartner] = useState(null);

  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allSubRegisters, setAllSubRegisters] = useState([]);

  const [formCities, setFormCities] = useState([]);
  const [formSubRegisters, setFormSubRegisters] = useState([]);

  const [formData, setFormData] = useState(EMPTY_FORM);

  // ─── Data Fetching ───────────────────────────────────────────────────────────

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setPartners(rows.map((p) => ({
        ...p,
        state_ids: (p.state_ids?.length ? p.state_ids : p.state_id != null ? [p.state_id] : []).map(Number),
        city_ids: (p.city_ids?.length ? p.city_ids : p.city_id != null ? [p.city_id] : []).map(Number),
        sub_registers: (p.sub_registers || []).map(Number),
      })));
    } catch {
      toast.error('Failed to fetch partners');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMasterData = async () => {
    try {
      const [statesRes, citiesRes, srRes] = await Promise.all([
        axios.get(`${API_URL}/states`),
        axios.get(`${API_URL}/cities`),
        axios.get(`${API_URL}/subregisters`),
      ]);
      const getArray = (resData) => Array.isArray(resData) ? resData : (Array.isArray(resData?.data) ? resData.data : []);
      setAllStates(getArray(statesRes.data));
      setAllCities(getArray(citiesRes.data));
      setAllSubRegisters(getArray(srRes.data));
    } catch {
      console.error('Error fetching master data');
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchAllMasterData();
  }, []);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const cityCatalog = useMemo(() => {
    const map = new Map();
    [...allCities, ...formCities].forEach((c) => {
      const nid = Number(c.id);
      if (Number.isFinite(nid)) map.set(nid, c);
    });
    return map;
  }, [allCities, formCities]);

  const getStateName  = (id) => allStates.find(s => Number(s.id) === Number(id))?.state_name || 'N/A';
  const getCityName   = (id) => cityCatalog.get(Number(id))?.city_name || 'N/A';
  const getSRName     = (id) => allSubRegisters.find(x => Number(x.id) === Number(id))?.sub_name
    || formSubRegisters.find(x => Number(x.id) === Number(id))?.sub_name
    || `SR #${id}`;

  // ─── Form Dependent Dropdown Logic (UNCHANGED) ───────────────────────────────

  const loadFormCities = async (stateId) => {
    if (!stateId) { setFormCities([]); setFormSubRegisters([]); return; }
    try {
      const res = await axios.get(`${API_URL}/cities?state_id=${stateId}`);
      setFormCities(Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []));
    } catch { console.error('Failed to load cities for form'); }
  };

  const loadFormSubRegisters = async (cityId) => {
    if (!cityId) { setFormSubRegisters([]); return; }
    try {
      const res = await axios.get(`${API_URL}/subregisters?city_id=${cityId}`);
      setFormSubRegisters(Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []));
    } catch { console.error('Failed to load sub-registers for form'); }
  };

  const loadSubRegistersForCities = async (cityIds) => {
    if (!cityIds.length) return [];
    const responses = await Promise.all(
      cityIds.map((id) => axios.get(`${API_URL}/subregisters?city_id=${id}`).catch(() => ({ data: [] }))),
    );
    let merged = [];
    responses.forEach((res) => {
      const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      merged = [...merged, ...data];
    });
    return merged.filter((item, index, self) => index === self.findIndex((x) => x.id === item.id));
  };

  const handleStateToggle = async (stateId) => {
    const sid = Number(stateId);
    const updatedStates = formData.state_ids.map(Number).includes(sid)
      ? formData.state_ids.filter((id) => Number(id) !== sid)
      : [...formData.state_ids, sid];

    try {
      let mergedCities = [];
      if (updatedStates.length) {
        const responses = await Promise.all(
          updatedStates.map((id) => axios.get(`${API_URL}/cities?state_id=${id}`)),
        );
        responses.forEach((res) => {
          const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
          mergedCities = [...mergedCities, ...data];
        });
        mergedCities = mergedCities.filter((item, index, self) => index === self.findIndex((x) => x.id === item.id));
      }
      const validCityIds = new Set(mergedCities.map((c) => Number(c.id)));
      const filteredCities = formData.city_ids.map(Number).filter((id) => validCityIds.has(id));
      const uniqueSR = await loadSubRegistersForCities(filteredCities);
      const validSrIds = new Set(uniqueSR.map((sr) => Number(sr.id)));

      setFormCities(mergedCities);
      setFormSubRegisters(uniqueSR);
      setFormData((prev) => ({
        ...prev,
        state_ids: updatedStates,
        city_ids: filteredCities,
        sub_registers: prev.sub_registers.map(Number).filter((id) => validSrIds.has(id)),
      }));
    } catch (err) {
      console.error('Failed loading cities', err);
      toast.error('Failed to load cities for selected states');
    }
  };

  const handleCityToggle = async (cityId) => {
    const cid = Number(cityId);
    const updatedCities = formData.city_ids.map(Number).includes(cid)
      ? formData.city_ids.filter((id) => Number(id) !== cid)
      : [...formData.city_ids, cid];

    if (updatedCities.length === 0) {
      setFormSubRegisters([]);
      setFormData((prev) => ({ ...prev, city_ids: [], sub_registers: [] }));
      return;
    }

    try {
      const unique = await loadSubRegistersForCities(updatedCities);
      const validSrIds = new Set(unique.map((sr) => Number(sr.id)));
      setFormSubRegisters(unique);
      setFormData((prev) => ({
        ...prev,
        city_ids: updatedCities,
        sub_registers: prev.sub_registers.map(Number).filter((id) => validSrIds.has(id)),
      }));
    } catch (err) {
      console.error('Failed loading sub-registers', err);
      toast.error('Failed to load sub-registers');
    }
  };

  const handleSubRegisterToggle = (id) => {
    const numId = Number(id);
    setFormData(prev => {
      const current = prev.sub_registers.map(Number);
      return {
        ...prev,
        sub_registers: current.includes(numId) ? current.filter(x => x !== numId) : [...current, numId]
      };
    });
  };

  const handleSetSubRegisters = (ids) => {
    setFormData((prev) => ({
      ...prev,
      sub_registers: [...new Set((ids || []).map(Number).filter(Number.isFinite))],
    }));
  };

  /** Batch-select states (Select All / filtered), then reload cities + SRs once. */
  const handleStatesSelectAll = async (ids) => {
    const addIds = (ids || []).map(Number).filter(Number.isFinite);
    if (!addIds.length) return;
    const updatedStates = [...new Set([...formData.state_ids.map(Number), ...addIds])];
    try {
      let mergedCities = [];
      const responses = await Promise.all(
        updatedStates.map((id) => axios.get(`${API_URL}/cities?state_id=${id}`).catch(() => ({ data: [] }))),
      );
      responses.forEach((res) => {
        const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        mergedCities = [...mergedCities, ...data];
      });
      mergedCities = mergedCities.filter((item, index, self) => index === self.findIndex((x) => x.id === item.id));
      const validCityIds = new Set(mergedCities.map((c) => Number(c.id)));
      const filteredCities = formData.city_ids.map(Number).filter((id) => validCityIds.has(id));
      const uniqueSR = await loadSubRegistersForCities(filteredCities);
      const validSrIds = new Set(uniqueSR.map((sr) => Number(sr.id)));
      setFormCities(mergedCities);
      setFormSubRegisters(uniqueSR);
      setFormData((prev) => ({
        ...prev,
        state_ids: updatedStates,
        city_ids: filteredCities,
        sub_registers: prev.sub_registers.map(Number).filter((id) => validSrIds.has(id)),
      }));
    } catch (err) {
      console.error('Failed selecting states', err);
      toast.error('Failed to load cities for selected states');
    }
  };

  const handleStatesClearAll = async (ids) => {
    const drop = new Set((ids || []).map(Number));
    const updatedStates = formData.state_ids.map(Number).filter((id) => !drop.has(id));
    if (!updatedStates.length) {
      setFormCities([]);
      setFormSubRegisters([]);
      setFormData((prev) => ({ ...prev, state_ids: [], city_ids: [], sub_registers: [] }));
      return;
    }
    try {
      let mergedCities = [];
      const responses = await Promise.all(
        updatedStates.map((id) => axios.get(`${API_URL}/cities?state_id=${id}`).catch(() => ({ data: [] }))),
      );
      responses.forEach((res) => {
        const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        mergedCities = [...mergedCities, ...data];
      });
      mergedCities = mergedCities.filter((item, index, self) => index === self.findIndex((x) => x.id === item.id));
      const validCityIds = new Set(mergedCities.map((c) => Number(c.id)));
      const filteredCities = formData.city_ids.map(Number).filter((id) => validCityIds.has(id));
      const uniqueSR = await loadSubRegistersForCities(filteredCities);
      const validSrIds = new Set(uniqueSR.map((sr) => Number(sr.id)));
      setFormCities(mergedCities);
      setFormSubRegisters(uniqueSR);
      setFormData((prev) => ({
        ...prev,
        state_ids: updatedStates,
        city_ids: filteredCities,
        sub_registers: prev.sub_registers.map(Number).filter((id) => validSrIds.has(id)),
      }));
    } catch (err) {
      console.error('Failed clearing states', err);
      toast.error('Failed to update states');
    }
  };

  const handleCitiesSelectAll = async (ids) => {
    const addIds = (ids || []).map(Number).filter(Number.isFinite);
    if (!addIds.length) return;
    const updatedCities = [...new Set([...formData.city_ids.map(Number), ...addIds])];
    try {
      const unique = await loadSubRegistersForCities(updatedCities);
      const validSrIds = new Set(unique.map((sr) => Number(sr.id)));
      setFormSubRegisters(unique);
      setFormData((prev) => ({
        ...prev,
        city_ids: updatedCities,
        sub_registers: prev.sub_registers.map(Number).filter((id) => validSrIds.has(id)),
      }));
    } catch (err) {
      console.error('Failed selecting cities', err);
      toast.error('Failed to load sub-registers');
    }
  };

  const handleCitiesClearAll = async (ids) => {
    const drop = new Set((ids || []).map(Number));
    const updatedCities = formData.city_ids.map(Number).filter((id) => !drop.has(id));
    if (!updatedCities.length) {
      setFormSubRegisters([]);
      setFormData((prev) => ({ ...prev, city_ids: [], sub_registers: [] }));
      return;
    }
    try {
      const unique = await loadSubRegistersForCities(updatedCities);
      const validSrIds = new Set(unique.map((sr) => Number(sr.id)));
      setFormSubRegisters(unique);
      setFormData((prev) => ({
        ...prev,
        city_ids: updatedCities,
        sub_registers: prev.sub_registers.map(Number).filter((id) => validSrIds.has(id)),
      }));
    } catch (err) {
      console.error('Failed clearing cities', err);
      toast.error('Failed to update cities');
    }
  };

  // ─── Modal Handlers (UNCHANGED) ──────────────────────────────────────────────

  const openCreate = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setEditingId(null);
    setFormData(JSON.parse(JSON.stringify(EMPTY_FORM)));
    setFormCities([]);
    setFormSubRegisters([]);
    setShowModal(true);
  };

  const resolvePartnerLocation = (partner) => {
    const state_ids = (partner.state_ids?.length
      ? partner.state_ids
      : partner.state_id != null ? [partner.state_id] : []).map(Number);
    const city_ids = (partner.city_ids?.length
      ? partner.city_ids
      : partner.city_id != null ? [partner.city_id] : []).map(Number);
    const sub_registers = (partner.sub_registers || []).map(Number);
    return { state_ids, city_ids, sub_registers };
  };

  const openEdit = async (partner) => {
    const { state_ids, city_ids, sub_registers } = resolvePartnerLocation(partner);
    setEditingId(partner.id);
    setFormData({
      name: partner.name || '',
      email: partner.email || '',
      password: '',
      phone: partner.phone || '',
      state_ids,
      city_ids,
      sub_registers,
      own_access: Boolean(partner.own_access),
      chat_access: partner.chat_access !== false,
      user_chat_access: partner.user_chat_access === true,
    });

    try {
      let mergedCities = [];
      if (state_ids.length) {
        const responses = await Promise.all(
          state_ids.map((id) => axios.get(`${API_URL}/cities?state_id=${id}`).catch(() => ({ data: [] }))),
        );
        responses.forEach((res) => {
          const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
          mergedCities = [...mergedCities, ...data];
        });
        mergedCities = mergedCities.filter((item, index, self) => index === self.findIndex((x) => x.id === item.id));
      }
      city_ids.forEach((cid) => {
        const found = allCities.find((c) => Number(c.id) === Number(cid));
        if (found && !mergedCities.some((c) => Number(c.id) === Number(cid))) mergedCities.push(found);
      });
      setFormCities(mergedCities);
      const allSR = await loadSubRegistersForCities(city_ids);
      setFormSubRegisters(allSR);
      setShowModal(true);
    } catch {
      toast.error('Failed to load partner location data');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(JSON.parse(JSON.stringify(EMPTY_FORM)));
    setFormCities([]);
    setFormSubRegisters([]);
  };

  // ─── CRUD (UNCHANGED) ────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sub_registers.length) {
      toast.error('Select at least one sub-register for this partner');
      return;
    }
    try {
      const token = getAuthToken();
      const payload = {
        ...formData,
        state_ids:     formData.state_ids.map(Number),
        city_ids:      formData.city_ids.map(Number),
        sub_registers: formData.sub_registers.map(Number),
        own_access:    Boolean(formData.own_access),
        chat_access:   Boolean(formData.chat_access),
        user_chat_access: formData.user_chat_access === true,
      };
      if (!payload.password) delete payload.password;
      if (editingId) {
        await axios.put(`${API_URL}/admin/partners/${editingId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Partner updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/partners`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Partner created successfully');
      }
      closeModal();
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/admin/partners/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Partner deleted');
      fetchPartners();
    } catch {
      toast.error('Failed to delete partner');
    }
  };

  const toggleOwnAccess = async (partner, nextVal) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/partners/${partner.id}`, { own_access: nextVal }, { headers: { Authorization: `Bearer ${token}` } });
      setPartners((prev) => prev.map((p) => (p.id === partner.id ? { ...p, own_access: nextVal } : p)));
      if (viewingPartner?.id === partner.id) setViewingPartner((v) => (v ? { ...v, own_access: nextVal } : v));
      toast.success('Own access updated');
    } catch {
      toast.error('Failed to update own access');
    }
  };

  const toggleUserChatAccess = async (partner, nextVal) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/partners/${partner.id}`, { user_chat_access: nextVal }, { headers: { Authorization: `Bearer ${token}` } });
      setPartners((prev) => prev.map((p) => (p.id === partner.id ? { ...p, user_chat_access: nextVal } : p)));
      if (viewingPartner?.id === partner.id) setViewingPartner((v) => (v ? { ...v, user_chat_access: nextVal } : v));
      toast.success(nextVal ? 'User chat access enabled' : 'User chat access turned off');
    } catch {
      toast.error('Failed to update user chat access');
    }
  };

  const toggleChatAccess = async (partner, nextVal) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/partners/${partner.id}`, { chat_access: nextVal }, { headers: { Authorization: `Bearer ${token}` } });
      setPartners((prev) => prev.map((p) => (p.id === partner.id ? { ...p, chat_access: nextVal } : p)));
      if (viewingPartner?.id === partner.id) setViewingPartner((v) => (v ? { ...v, chat_access: nextVal } : v));
      toast.success(nextVal ? 'Chat access enabled' : 'Chat access turned off');
    } catch {
      toast.error('Failed to update chat access');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="admin-page">
      <div className="page-header flex-header">
        <div>
          <h1>Partner Management</h1>
          <p>Create and manage partners with sub-register access</p>
        </div>
        <button type="button" id="add-partner-btn" className="btn-primary" onClick={openCreate} style={{ cursor: 'pointer', zIndex: 10 }}>
          <FiUserPlus /> Add New Partner
        </button>
      </div>

      {/* ── Table ── */}
      <div className="admin-card table-section">
        {loading ? (
          <div className="skeleton" style={{ height: '300px', borderRadius: '8px' }}></div>
        ) : partners.length === 0 ? (
          <div className="empty-state">
            <FiInbox size={48} />
            <h3>No Partners Found</h3>
            <p>Get started by adding your first partner.</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name / Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Sub Registers</th>
                <th>Own Access</th>
                <th>Access Chat</th>
                <th>User Chat Access</th>
                <th>Property Count</th>
                <th>KYC</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id}>
                  <td><span className="hybrid-id">#{p.id}</span></td>
                  <td>
                    <div className="table-cell-title">
                      <strong>{p.name}</strong>
                      <span>{p.email}</span>
                    </div>
                  </td>
                  <td>{p.phone || 'N/A'}</td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {(() => {
                      const states = p.state_ids?.length ? p.state_ids : (p.state_id != null ? [p.state_id] : []);
                      const cities = p.city_ids?.length ? p.city_ids : (p.city_id != null ? [p.city_id] : []);
                      return (
                        <>
                          {states.length > 0 ? states.map((id) => getStateName(id)).join(', ') : '—'}
                          {cities.length > 0 && (
                            <span style={{ color: 'var(--admin-text-muted)', display: 'block' }}>
                              {cities.map((id) => getCityName(id)).join(', ')}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="sub-reg-tags">
                      {p.sub_registers?.length > 0
                        ? p.sub_registers.map(srId => (
                            <span key={srId} className="tag-sr">{getSRName(srId)}</span>
                          ))
                        : 'None'}
                    </div>
                  </td>
                  <td>
                    <label className="toggle-label" style={{ width: 'fit-content' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(p.own_access)}
                        onChange={(e) => toggleOwnAccess(p, e.target.checked)}
                      />
                      <div className="toggle-switch"></div>
                    </label>
                  </td>
                  <td>
                    <label className="toggle-label" style={{ width: 'fit-content' }}>
                      <input
                        type="checkbox"
                        checked={p.chat_access !== false}
                        onChange={(e) => toggleChatAccess(p, e.target.checked)}
                        aria-label={`Access Chat for ${p.name || 'partner'}`}
                      />
                      <div className="toggle-switch"></div>
                    </label>
                  </td>
                  <td>
                    <label className="toggle-label" style={{ width: 'fit-content' }}>
                      <input
                        type="checkbox"
                        checked={p.user_chat_access === true}
                        onChange={(e) => toggleUserChatAccess(p, e.target.checked)}
                        aria-label={`User Chat Access for ${p.name || 'partner'}`}
                      />
                      <div className="toggle-switch"></div>
                    </label>
                  </td>
                  <td><span className="info-pill">{p.property_count || 0}</span></td>
                  <td>
                    <span className={`badge-role ${p.kyc_status === 'approved' ? 'partner' : p.kyc_status === 'rejected' ? 'admin' : 'user'}`}>
                      {p.kyc_status === 'not_submitted' ? 'Not Submitted' : (p.kyc_status || 'not_submitted')}
                    </span>
                  </td>
                  <td><span className={p.is_active ? 'badge-role partner' : 'badge-role user'}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-icon" title="View Details" onClick={() => setViewingPartner(p)}><FiEye /></button>
                      <button className="btn-icon" title="Edit Partner" onClick={() => openEdit(p)}><FiEdit2 /></button>
                      <button className="btn-icon text-danger" title="Delete Partner" onClick={() => handleDelete(p.id)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="admin-modal-overlay animate-fade-in" style={{ zIndex: 9999 }} onClick={closeModal}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Partner Details' : 'Register New Partner'}</h2>
              <button className="btn-icon" onClick={closeModal}><FiX /></button>
            </div>
            <div style={{ overflowY: 'auto', padding: '1.5rem' }}>
              <form onSubmit={handleSubmit} className="modal-form" style={{ padding: 0 }}>

                {/* ── Basic Info ── */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input type="text" required placeholder="Enter partner name" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input type="email" required placeholder="partner@company.com" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password {editingId ? '(Leave blank to keep current)' : '*'}</label>
                    <input type="password" required={!editingId} placeholder={editingId ? 'Update password (optional)' : 'Secure password'} value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input type="text" required placeholder="Contact phone" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', display: 'block' }}>Own Access</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                      <label className="toggle-label" style={{ margin: 0, width: 'fit-content' }}>
                        <input type="checkbox" checked={Boolean(formData.own_access)} onChange={(e) => setFormData((prev) => ({ ...prev, own_access: e.target.checked }))} />
                        <div className="toggle-switch"></div>
                      </label>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>
                        When off, this partner cannot see Own Properties, Add Own Property, or Add Own Project.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', display: 'block' }}>Access Chat</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                      <label className="toggle-label" style={{ margin: 0, width: 'fit-content' }}>
                        <input type="checkbox" checked={formData.chat_access !== false} onChange={(e) => setFormData((prev) => ({ ...prev, chat_access: e.target.checked }))} />
                        <div className="toggle-switch"></div>
                      </label>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>
                        When on, this partner can chat with admin. User chat also requires this to be on.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', display: 'block' }}>User Chat Access</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                      <label className="toggle-label" style={{ margin: 0, width: 'fit-content' }}>
                        <input type="checkbox" checked={formData.user_chat_access === true} onChange={(e) => setFormData((prev) => ({ ...prev, user_chat_access: e.target.checked }))} />
                        <div className="toggle-switch"></div>
                      </label>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>
                        Off by default. When on, and Chat Access is also on, this partner can message users.
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── Location Dropdowns ── */}
                <div className="form-divider">Assign Sub-Registers</div>

                <div className="form-row">
                  {/* STATE MULTI-SELECT DROPDOWN */}
                  <div className="form-group">
                    <MultiSelectDropdown
                      label="Select States"
                      icon={FiMap}
                      options={allStates}
                      selectedIds={formData.state_ids}
                      onToggle={handleStateToggle}
                      onSelectAll={handleStatesSelectAll}
                      onClearAll={handleStatesClearAll}
                      placeholder="Choose states..."
                      emptyText="No states available"
                      resolveLabel={getStateName}
                    />
                  </div>

                  {/* CITY MULTI-SELECT DROPDOWN */}
                  <div className="form-group">
                    <MultiSelectDropdown
                      label="Select Cities"
                      icon={FiMapPin}
                      options={formCities}
                      selectedIds={formData.city_ids}
                      onToggle={handleCityToggle}
                      onSelectAll={handleCitiesSelectAll}
                      onClearAll={handleCitiesClearAll}
                      placeholder="Choose cities..."
                      emptyText="Select a state first"
                      resolveLabel={getCityName}
                    />
                  </div>
                </div>

                {/* ── Sub-Registers grouped by City ── */}
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--admin-text)' }}>
                    Sub-Registers
                    {formData.sub_registers.length > 0 && (
                      <span className="sr-selected-badge">{formData.sub_registers.length} selected</span>
                    )}
                  </label>
                  <CityGroupedSubRegisters
                    formSubRegisters={formSubRegisters}
                    selectedCityIds={formData.city_ids}
                    selectedSRIds={formData.sub_registers}
                    onToggleSR={handleSubRegisterToggle}
                    onSetSRs={handleSetSubRegisters}
                    getCityName={getCityName}
                  />
                </div>

                <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
                  <button type="submit" className="btn-submit" style={{ flex: 1, marginTop: 0 }}>
                    {editingId ? 'Update Partner Account' : 'Create Partner Account'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal} style={{ padding: '1rem' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── View Details Modal (UNCHANGED) ── */}
      {viewingPartner && (
        <div className="admin-modal-overlay animate-fade-in" style={{ zIndex: 9999 }} onClick={() => setViewingPartner(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>Partner Details</h2>
              <button className="btn-icon" onClick={() => setViewingPartner(null)}><FiX /></button>
            </div>
            <div className="modal-form">
              <div className="filter-box-grid" style={{ gap: '2rem' }}>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Partner Name</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{viewingPartner.name}</p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Email Address</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{viewingPartner.email}</p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Phone Number</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{viewingPartner.phone || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Status</label>
                  <p style={{ marginTop: '0.25rem' }}>
                    <span className={viewingPartner.is_active ? 'badge-role partner' : 'badge-role user'}>
                      {viewingPartner.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Own Access</label>
                  <p style={{ marginTop: '0.25rem' }}>
                    <span className={viewingPartner.own_access ? 'badge-role partner' : 'badge-role user'}>
                      {viewingPartner.own_access ? 'Enabled' : 'Disabled'}
                    </span>
                  </p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Access Chat</label>
                  <p style={{ marginTop: '0.25rem' }}>
                    <span className={viewingPartner.chat_access !== false ? 'badge-role partner' : 'badge-role user'}>
                      {viewingPartner.chat_access !== false ? 'Enabled' : 'Disabled'}
                    </span>
                  </p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>User Chat Access</label>
                  <p style={{ marginTop: '0.25rem' }}>
                    <span className={viewingPartner.user_chat_access === true ? 'badge-role partner' : 'badge-role user'}>
                      {viewingPartner.user_chat_access === true ? 'Enabled' : 'Disabled'}
                    </span>
                  </p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>States</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>
                    {(() => {
                      const states = viewingPartner.state_ids?.length
                        ? viewingPartner.state_ids
                        : viewingPartner.state_id != null ? [viewingPartner.state_id] : [];
                      return states.length ? states.map((id) => getStateName(id)).join(', ') : 'N/A';
                    })()}
                  </p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Cities</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>
                    {(() => {
                      const cities = viewingPartner.city_ids?.length
                        ? viewingPartner.city_ids
                        : viewingPartner.city_id != null ? [viewingPartner.city_id] : [];
                      return cities.length ? cities.map((id) => getCityName(id)).join(', ') : 'N/A';
                    })()}
                  </p>
                </div>
                <div className="detail-item" style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Assigned Sub-Registers</label>
                  <div className="sub-reg-tags" style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {viewingPartner.sub_registers?.length > 0
                      ? viewingPartner.sub_registers.map(srId => (
                          <span key={srId} className="tag-sr" style={{ padding: '0.25rem 0.75rem', background: '#f1f5f9', borderRadius: '0.5rem' }}>
                            {getSRName(srId)}
                          </span>
                        ))
                      : 'No sub-registers assigned'}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => { setViewingPartner(null); openEdit(viewingPartner); }}>
                  <FiEdit2 style={{ marginRight: '0.4rem' }} /> Edit Partner
                </button>
                <button className="btn-secondary" onClick={() => setViewingPartner(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerManagement;